import { Component } from '@angular/core';
import { TTranslationKey } from 'src/app/project/features/translate/types';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { DropdownService } from '../../dropdown/dropdown-service/dropdown.service';
import { TDropdown } from '../../dropdown/dropdown.consts';

export type IChildOption = {
  label: string;
  id: string;
  isSelected: boolean;
  isVisible: boolean;
};

export type IParentOption = {
  label: string;
  id: string;
  isSelected: boolean;
  isCollapsed: boolean;
  isIndeterminate: boolean;
  children: IChildOption[];
  isVisible: boolean;
};

@Component({
  selector: 'pp-multi-select-dropdown',
  templateUrl: './grouped-multi-select-dropdown.component.html',
  styleUrls: ['./grouped-multi-select-dropdown.component.scss'],
  standalone: false,
})
export class GroupedMultiSelectDropdownComponent {
  dropdown: TDropdown = this.dropdownService.getDropdown();
  parentTitle: TTranslationKey;
  EIconPath = EIconPath;
  parentOptions: IParentOption[] = [];
  isAllCollapsed = true;
  isAllSelected = false;
  placeholderText: TTranslationKey;
  keyword = '';

  constructor(private dropdownService: DropdownService) {
    this.parentTitle = this.dropdown.data.parentTitle;
    this.placeholderText = this.dropdown.data.placeholderText;
    this.parentOptions = this.dropdown.data?.parentOptions ? this.dropdown.data.parentOptions : [];
  }

  setKeyword(value: string): void {
    this.keyword = value;

    this.filterItems();
  }

  handleExpandParentOptions(optionId: string): void {
    const currentParent = this.parentOptions.find((parent) => parent.id === optionId);
    currentParent.isSelected = currentParent.children.some((child) => child.isSelected);
    currentParent.isCollapsed = !currentParent.isCollapsed;

    this.handleSelectedAndCollapsedState();
  }

  toggleParentOption(optionId: string): void {
    const currentParent = this.parentOptions.find((parent) => parent.id === optionId);
    currentParent.isSelected = !currentParent.isSelected;
    currentParent.children.map((child) => (child.isSelected = currentParent.isSelected));
    this.handleIndeterminateStatus(currentParent);
    this.updateStateAndNotifyListener();
  }

  toggleChildOption(optionId: string, parentId: string): void {
    const currentParent = this.parentOptions.find((parent) => parent.id === parentId);
    const currentChild = currentParent.children.find((child) => child.id === optionId);
    currentChild.isSelected = !currentChild.isSelected;

    const isEveryChildSelected = currentParent.children.every((child) => child.isSelected);
    currentParent.isSelected = isEveryChildSelected;

    this.handleIndeterminateStatus(currentParent);
    this.updateStateAndNotifyListener();
  }

  handleIndeterminateStatus(parent: IParentOption): void {
    const isEveryChildSelected = parent.children.every((child) => child.isSelected);
    parent.isIndeterminate =
      !isEveryChildSelected && parent.children.some((child) => child.isSelected);
  }

  handleBulkSelection(selected: boolean): void {
    this.parentOptions.map((parentOption) => {
      parentOption.isSelected = selected;
      parentOption.isIndeterminate = false;

      parentOption.children = parentOption.children.map((child) => ({
        ...child,
        isSelected: selected,
      }));
    });
    this.updateStateAndNotifyListener();
  }

  handleBulkCollapse(isCollapsed: boolean): void {
    this.parentOptions.map((parentOption) => {
      parentOption.isCollapsed = isCollapsed;
    });
    this.handleSelectedAndCollapsedState();
    this.filterItems();
  }

  handleFormatSelectedOptions(): IChildOption[] {
    const selectedOptions = [];
    this.parentOptions.forEach((parent) => {
      parent.children.forEach((child) => {
        if (child.isSelected) {
          selectedOptions.push(child);
        }
      });
    });

    return selectedOptions;
  }

  handleSaveSelected(): void {
    const selectedOptions = this.handleFormatSelectedOptions();

    this.dropdown.callback(selectedOptions);
    this.dropdownService.clearDropdown();
  }

  private handleSelectedAndCollapsedState(): void {
    this.isAllSelected = this.parentOptions.every(
      (parent) => parent.isSelected && parent.children.every((child) => !!child.isSelected),
    );

    this.isAllCollapsed = this.parentOptions.every((parent) => !!parent.isCollapsed);
  }
  private updateStateAndNotifyListener(): void {
    this.handleSelectedAndCollapsedState();
    const selectedOptions = this.handleFormatSelectedOptions();
    this.dropdown.callback(selectedOptions);
    this.filterItems();
  }

  private filterItems(): void {
    if (this.keyword === '') {
      this.parentOptions.forEach((parent) => {
        parent.isVisible = true;

        parent.children.forEach((child) => {
          child.isVisible = true;
        });
      });
      return;
    }

    this.parentOptions = this.parentOptions.map((parentOption: IParentOption) => {
      const parentLabelMatches = parentOption.label
        .toLowerCase()
        .includes(this.keyword.toLowerCase());

      if (parentLabelMatches) {
        parentOption.children.forEach((child) => {
          child.isVisible = true;
        });

        parentOption.isVisible = true;
      } else {
        parentOption.children.forEach((child) => {
          child.isVisible = child.label.toLowerCase().includes(this.keyword.toLowerCase());
        });

        parentOption.isVisible = parentOption.children.some((child) => child.isVisible);
      }

      return {
        ...parentOption,
      };
    });
  }
}
